import { Controller } from "@hotwired/stimulus";
import maplibregl from "maplibre-gl";

// Church location - The Field Church NOLA
const CHURCH_LOCATION = [-90.079694, 30.382594];
const CHURCH_ZOOM = 12; // ~5 mile radius view

export default class extends Controller {
  static targets = ["container", "personTemplate", "popupTemplate", "churchPopupTemplate"];
  static values = {
    people: Array,
    currentPersonId: Number,
  };

  connect() {
    this.initialCenter = this.getInitialCenter();
    this.initialZoom = CHURCH_ZOOM;
    this.searchQuery = this.getQueryParam("q") || "";
    this.initializeMap();
    this.setupSearchListener();
  }

  getQueryParam(name) {
    const params = new URLSearchParams(window.location.search);
    return params.get(name);
  }

  updateQueryParam(query) {
    const url = new URL(window.location);
    if (query) {
      url.searchParams.set("q", query);
    } else {
      url.searchParams.delete("q");
    }
    window.history.replaceState({}, "", url);
  }

  disconnect() {
    if (this.map) {
      this.map.remove();
    }
    if (this.searchInput) {
      this.searchInput.removeEventListener("input", this.handleSearch);
    }
  }

  setupSearchListener() {
    this.searchInput = document.getElementById("map-search-input");
    if (this.searchInput) {
      // Initialize input with query param value
      if (this.searchQuery) {
        this.searchInput.value = this.searchQuery;
      }
      this.handleSearch = this.handleSearch.bind(this);
      this.searchInput.addEventListener("input", this.handleSearch);
    }
  }

  handleSearch(event) {
    const query = event.target.value.toLowerCase().trim();
    if (query === this.searchQuery) return;

    this.searchQuery = query;
    this.updateQueryParam(query);
    this.updatePeopleLayer();
  }

  filterPeople(people) {
    if (!this.searchQuery) return people;

    return people.filter((person) => {
      const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
      return fullName.includes(this.searchQuery);
    });
  }

  updatePeopleLayer() {
    const filteredPeople = this.filterPeople(this.peopleValue);
    const locationGroups = this.groupPeopleByLocation(filteredPeople);
    const currentPersonId = this.currentPersonIdValue;

    const geojson = {
      type: "FeatureCollection",
      features: locationGroups.map((group, index) => {
        const hasCurrentUser = group.people.some((p) => p.id === currentPersonId);
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [group.longitude, group.latitude],
          },
          properties: {
            id: index,
            hasCurrentUser: hasCurrentUser,
            people: JSON.stringify(
              group.people.map((p) => ({
                id: p.id,
                name: `${p.first_name} ${p.last_name}`,
                phone: p.phone || "",
                email: p.email || "",
                photo_url: p.avatar_url || p.photo_url || "",
              }))
            ),
          },
        };
      }),
    };

    // Update the source data
    const source = this.map.getSource("people");
    if (source) {
      source.setData(geojson);
    }

    // Adjust view based on search state
    if (!this.searchQuery) {
      // Search cleared - reset to initial view
      this.map.flyTo({
        center: this.initialCenter,
        zoom: this.initialZoom,
        duration: 500,
      });
    } else if (locationGroups.length > 0) {
      // Fit bounds to show all filtered results
      this.fitBoundsToLocations(locationGroups);
    }
  }

  fitBoundsToLocations(locationGroups) {
    if (locationGroups.length === 0) return;

    if (locationGroups.length === 1) {
      // Single location - center on it
      const group = locationGroups[0];
      this.map.flyTo({
        center: [group.longitude, group.latitude],
        zoom: 14,
        duration: 500,
      });
      return;
    }

    // Multiple locations - calculate bounds
    const bounds = new maplibregl.LngLatBounds();
    locationGroups.forEach((group) => {
      bounds.extend([group.longitude, group.latitude]);
    });

    this.map.fitBounds(bounds, {
      padding: 80,
      duration: 500,
      maxZoom: 14,
    });
  }

  getInitialCenter() {
    const currentPersonId = this.currentPersonIdValue;
    if (currentPersonId) {
      const currentPerson = this.peopleValue.find((p) => p.id === currentPersonId);
      if (currentPerson && currentPerson.latitude && currentPerson.longitude) {
        return [parseFloat(currentPerson.longitude), parseFloat(currentPerson.latitude)];
      }
    }
    return CHURCH_LOCATION;
  }

  initializeMap() {
    this.map = new maplibregl.Map({
      container: this.containerTarget,
      style: {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution:
              '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
          },
        ],
      },
      center: this.getInitialCenter(),
      zoom: CHURCH_ZOOM,
    });

    this.map.addControl(new maplibregl.NavigationControl(), "top-right");

    this.map.on("load", () => {
      this.addChurchMarker();
      this.addPeopleLayer();

      // If there's an initial search query, fit bounds to filtered results
      if (this.searchQuery) {
        const filteredPeople = this.filterPeople(this.peopleValue);
        const locationGroups = this.groupPeopleByLocation(filteredPeople);
        if (locationGroups.length > 0) {
          this.fitBoundsToLocations(locationGroups);
        }
      }
    });
  }

  addChurchMarker() {
    this.map.addSource("church", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: CHURCH_LOCATION,
        },
        properties: {
          name: "The Field Church",
        },
      },
    });

    this.map.addLayer({
      id: "church-pin",
      type: "circle",
      source: "church",
      paint: {
        "circle-radius": 10,
        "circle-color": "#2D3E50",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    });

    this.map.on("click", "church-pin", (e) => {
      const coordinates = e.features[0].geometry.coordinates.slice();
      const content = this.churchPopupTemplateTarget.content.cloneNode(true);

      new maplibregl.Popup({ offset: 25 })
        .setLngLat(coordinates)
        .setDOMContent(content)
        .addTo(this.map);
    });

    this.map.on("mouseenter", "church-pin", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });

    this.map.on("mouseleave", "church-pin", () => {
      this.map.getCanvas().style.cursor = "";
    });
  }

  groupPeopleByLocation(people) {
    const groups = new Map();

    people.forEach((person) => {
      if (person.latitude && person.longitude) {
        const lat = parseFloat(person.latitude);
        const lng = parseFloat(person.longitude);
        const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;

        if (groups.has(key)) {
          groups.get(key).people.push(person);
        } else {
          groups.set(key, {
            longitude: lng,
            latitude: lat,
            people: [person],
          });
        }
      }
    });

    return Array.from(groups.values());
  }

  addPeopleLayer() {
    const filteredPeople = this.filterPeople(this.peopleValue);
    const locationGroups = this.groupPeopleByLocation(filteredPeople);

    // Check if current user is at each location
    const currentPersonId = this.currentPersonIdValue;

    const geojson = {
      type: "FeatureCollection",
      features: locationGroups.map((group, index) => {
        const hasCurrentUser = group.people.some((p) => p.id === currentPersonId);
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [group.longitude, group.latitude],
          },
          properties: {
            id: index,
            hasCurrentUser: hasCurrentUser,
            people: JSON.stringify(
              group.people.map((p) => ({
                id: p.id,
                name: `${p.first_name} ${p.last_name}`,
                phone: p.phone || "",
                email: p.email || "",
                photo_url: p.avatar_url || p.photo_url || "",
              }))
            ),
          },
        };
      }),
    };

    this.map.addSource("people", {
      type: "geojson",
      data: geojson,
    });

    // Add circle layer for markers - same size for all, different color for current user
    this.map.addLayer({
      id: "people-pins",
      type: "circle",
      source: "people",
      paint: {
        "circle-radius": 10,
        "circle-color": [
          "case",
          ["get", "hasCurrentUser"],
          "#6B6F76",
          "#B7995B",
        ],
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    });

    this.map.on("click", "people-pins", (e) => {
      const feature = e.features[0];
      const coordinates = feature.geometry.coordinates.slice();
      const props = feature.properties;
      const people = JSON.parse(props.people);

      const popupContent = this.createPopupContent(people);

      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      new maplibregl.Popup({ offset: 25, maxWidth: "320px" })
        .setLngLat(coordinates)
        .setDOMContent(popupContent)
        .addTo(this.map);
    });

    this.map.on("mouseenter", "people-pins", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });

    this.map.on("mouseleave", "people-pins", () => {
      this.map.getCanvas().style.cursor = "";
    });
  }

  createPopupContent(people) {
    const popup = this.popupTemplateTarget.content.cloneNode(true);
    const container = popup.querySelector("[data-popup-people]");

    people.forEach((person) => {
      const row = this.personTemplateTarget.content.cloneNode(true);
      row.querySelector("[data-person-link]").href = `/people/${person.id}`;
      row.querySelector("[data-person-name]").textContent = person.name;

      const contact = row.querySelector("[data-person-contact]");
      if (person.phone || person.email) {
        contact.textContent = person.phone || person.email;
      } else {
        contact.remove();
      }

      const avatarImg = row.querySelector("[data-person-avatar-img]");
      const avatarPlaceholder = row.querySelector("[data-person-avatar-placeholder]");
      if (person.photo_url) {
        avatarImg.src = person.photo_url;
        avatarImg.alt = person.name;
        avatarImg.style.display = "";
        avatarPlaceholder.remove();
      } else {
        avatarImg.remove();
      }

      container.appendChild(row);
    });

    return popup;
  }
}
